/**
 * useWordPronunciation — PWA port of
 * client-native/src/features/flight-school/hooks/useWordPronunciation.ts.
 *
 * Plays the bundled native-speaker recordings for Spanish words, queues rapid
 * taps so every word is heard sequentially, and persists the mute toggle
 * across activities (iOS keeps it in globalStore.isFlightSchoolMuted; the PWA
 * uses localStorage).
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { getWordAudio, hasWordAudio } from "../assets/audio/words";

const MUTE_STORAGE_KEY = "parrot.flightSchoolMuted";

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

interface UseWordPronunciationReturn {
  isPlaying: boolean;
  isMuted: boolean;
  playWord: (wordId: string) => void;
  toggleMute: () => void;
}

export function useWordPronunciation(): UseWordPronunciationReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(readMuted);

  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const queueRef = useRef<string[]>([]);
  const isProcessingRef = useRef(false);
  const isMutedRef = useRef(isMuted);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const playSingleWord = useCallback(async (wordId: string) => {
    const src = getWordAudio(wordId);
    if (!src) {
      console.warn(`No audio file for word: ${wordId}`);
      return;
    }

    currentAudioRef.current?.pause();
    const audio = new Audio(src);
    currentAudioRef.current = audio;
    audio.volume = 1;

    await new Promise<void>((resolve) => {
      const done = () => {
        if (currentAudioRef.current === audio) currentAudioRef.current = null;
        resolve();
      };
      audio.addEventListener("ended", done, { once: true });
      audio.addEventListener("error", done, { once: true });
      audio.play().catch(() => done());
      // Fallback in case neither event fires (matches iOS's 3s fallback).
      setTimeout(done, 3000);
    });
  }, []);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    if (isMountedRef.current) setIsPlaying(true);

    while (queueRef.current.length > 0) {
      if (isMutedRef.current) {
        queueRef.current = [];
        break;
      }
      const nextWord = queueRef.current.shift()!;
      await playSingleWord(nextWord);
    }

    isProcessingRef.current = false;
    if (isMountedRef.current) setIsPlaying(false);
  }, [playSingleWord]);

  const playWord = useCallback(
    (wordId: string) => {
      if (isMutedRef.current) return;
      if (!hasWordAudio(wordId)) {
        console.warn(`No audio file for word: ${wordId}`);
        return;
      }
      // Prevent spam: skip duplicates and cap queue at 3 (iOS parity).
      if (queueRef.current.includes(wordId)) return;
      if (queueRef.current.length >= 3) return;

      queueRef.current.push(wordId);
      if (!isProcessingRef.current) {
        void processQueue();
      }
    },
    [processQueue],
  );

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(MUTE_STORAGE_KEY, String(next));
      } catch {
        /* storage unavailable */
      }
      if (next) queueRef.current = [];
      return next;
    });
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      // Don't clear the queue on unmount — let queued audio finish (iOS parity).
      isMountedRef.current = false;
    };
  }, []);

  return { isPlaying, isMuted, playWord, toggleMute };
}
